/**
 * Reactive UI Update System
 * Provides automatic UI updates based on application state changes
 */

/**
 * @typedef {Object} UIBinding
 * @property {string} selector - CSS selector for the element
 * @property {string} property - State property to bind to
 * @property {function} [transform] - Optional transform function for the value
 * @property {string} [attribute] - Attribute to update (default: textContent)
 * @property {boolean} [immediate] - Whether to update immediately on state change
 */

/**
 * @typedef {Object} ConditionalBinding
 * @property {string} selector - CSS selector for the element
 * @property {string} property - State property to watch
 * @property {Object} conditions - Condition-to-action mapping
 * @property {string} [attribute] - Attribute to update (default: className)
 */

/**
 * Reactive UI System that automatically updates DOM elements based on state changes
 */
export class UIReactiveSystem {
  constructor(stateManager) {
    if (!stateManager) {
      throw new Error('StateManager is required');
    }

    this.stateManager = stateManager;
    this.bindings = new Map();
    this.conditionalBindings = new Map();
    this.computedBindings = new Map();
    this.unsubscribeFunctions = new Set();
    this.isDestroyed = false;
    
    // Performance tracking
    this.updateCount = 0;
    this.lastUpdateTime = 0;
    this.averageUpdateTime = 0;
    
    // Batch update system
    this.pendingUpdates = new Set();
    this.updateTimer = null;
    
    // Bind methods
    this.destroy = this.destroy.bind(this);
    this._performBatchUpdate = this._performBatchUpdate.bind(this);
  }

  /**
   * Bind a DOM element to a state property
   * @param {UIBinding} binding - Binding configuration
   * @returns {function} Unbind function
   */
  bind(binding) {
    if (this.isDestroyed) {
      console.warn('Cannot bind to destroyed UIReactiveSystem');
      return () => {};
    }

    const { selector, property, transform, attribute = 'textContent', immediate = false } = binding;
    
    if (!selector || !property) {
      throw new Error('Selector and property are required for binding');
    }

    const bindingId = `${selector}:${property}:${attribute}`;
    
    // Store binding configuration
    this.bindings.set(bindingId, {
      selector,
      property,
      transform: transform || (value => value),
      attribute,
      immediate,
      lastValue: undefined
    });

    // Subscribe to state changes
    const unsubscribe = this.stateManager.subscribe(property, (oldValue, newValue) => {
      this._scheduleElementUpdate(bindingId, newValue);
    });

    this.unsubscribeFunctions.add(unsubscribe);

    // Initial update
    const currentValue = this.stateManager.get(property);
    this._scheduleElementUpdate(bindingId, currentValue);

    // Return unbind function
    return () => {
      this.bindings.delete(bindingId);
      unsubscribe();
      this.unsubscribeFunctions.delete(unsubscribe);
    };
  }

  /**
   * Bind multiple elements at once
   * @param {UIBinding[]} bindings - Array of binding configurations
   * @returns {function} Unbind all function
   */
  bindMultiple(bindings) {
    if (this.isDestroyed) {
      console.warn('Cannot bind to destroyed UIReactiveSystem');
      return () => {};
    }

    const unbindFunctions = bindings.map(binding => this.bind(binding));

    return () => {
      unbindFunctions.forEach(unbind => unbind());
    };
  }

  /**
   * Bind conditional styling/classes based on state
   * @param {ConditionalBinding} binding - Conditional binding configuration
   * @returns {function} Unbind function
   */
  bindConditional(binding) {
    if (this.isDestroyed) {
      console.warn('Cannot bind to destroyed UIReactiveSystem');
      return () => {};
    }

    const { selector, property, conditions, attribute = 'className' } = binding;
    
    if (!selector || !property || !conditions) {
      throw new Error('Selector, property, and conditions are required for conditional binding');
    }

    const bindingId = `conditional:${selector}:${property}:${attribute}`;
    
    // Store binding configuration
    this.conditionalBindings.set(bindingId, {
      selector,
      property,
      conditions,
      attribute,
      lastValue: undefined
    });

    // Subscribe to state changes
    const unsubscribe = this.stateManager.subscribe(property, (oldValue, newValue) => {
      this._scheduleConditionalUpdate(bindingId, newValue);
    });

    this.unsubscribeFunctions.add(unsubscribe);

    // Initial update
    const currentValue = this.stateManager.get(property);
    this._scheduleConditionalUpdate(bindingId, currentValue);

    // Return unbind function
    return () => {
      this.conditionalBindings.delete(bindingId);
      unsubscribe();
      this.unsubscribeFunctions.delete(unsubscribe);
    };
  }

  /**
   * Bind computed values that depend on multiple state properties
   * @param {string} selector - CSS selector for the element
   * @param {string[]} properties - Array of state properties to watch
   * @param {function} computeFn - Function to compute the value from state properties
   * @param {string} [attribute='textContent'] - Attribute to update
   * @returns {function} Unbind function
   */
  bindComputed(selector, properties, computeFn, attribute = 'textContent') {
    if (this.isDestroyed) {
      console.warn('Cannot bind to destroyed UIReactiveSystem');
      return () => {};
    }

    if (!selector || !properties || !computeFn) {
      throw new Error('Selector, properties, and computeFn are required for computed binding');
    }

    const bindingId = `computed:${selector}:${properties.join(',')}:${attribute}`;
    
    // Store binding configuration
    this.computedBindings.set(bindingId, {
      selector,
      properties,
      computeFn,
      attribute,
      lastValue: undefined
    });

    // Subscribe to all relevant properties
    const unsubscribeFunctions = properties.map(property => {
      return this.stateManager.subscribe(property, () => {
        this._scheduleComputedUpdate(bindingId);
      });
    });

    unsubscribeFunctions.forEach(unsub => this.unsubscribeFunctions.add(unsub));

    // Initial update
    this._scheduleComputedUpdate(bindingId);

    // Return unbind function
    return () => {
      this.computedBindings.delete(bindingId);
      unsubscribeFunctions.forEach(unsub => {
        unsub();
        this.unsubscribeFunctions.delete(unsub);
      });
    };
  }

  /**
   * Set up common UI bindings for the app
   */
  setupCommonBindings() {
    if (this.isDestroyed) {
      return;
    }

    // Location status binding
    this.bindConditional({
      selector: '.status-indicator',
      property: 'locationStatus',
      conditions: {
        'available': 'status-indicator available',
        'pending': 'status-indicator pending',
        'denied': 'status-indicator denied',
        'error': 'status-indicator error'
      }
    });

    // Online/offline status
    this.bindConditional({
      selector: 'body',
      property: 'isOnline',
      conditions: {
        true: 'online',
        false: 'offline'
      },
      attribute: 'className'
    });

    // Error message display
    this.bind({
      selector: '.error-text',
      property: 'errorMessage',
      transform: (message) => message || ''
    });

    // Solar data availability
    this.bindConditional({
      selector: '.solar-data',
      property: 'solarDataAvailable',
      conditions: {
        true: 'solar-data available',
        false: 'solar-data unavailable'
      }
    });

    // App initialization state
    this.bindConditional({
      selector: '.app-container',
      property: 'isInitialized',
      conditions: {
        true: 'app-container initialized',
        false: 'app-container initializing'
      }
    });

    // Battery optimization indicator
    this.bindConditional({
      selector: '.battery-indicator',
      property: 'battery.lowPowerMode',
      conditions: {
        true: 'battery-indicator low-power',
        false: 'battery-indicator normal'
      }
    });

    // Performance metrics (for debugging)
    this.bindComputed(
      '.debug-performance',
      ['performance.updateCount', 'performance.errorCount'],
      (updateCount, errorCount) => {
        return `Updates: ${updateCount || 0}, Errors: ${errorCount || 0}`;
      }
    );

    // Last updated timestamp
    this.bind({
      selector: '#last-updated',
      property: 'lastCalculationTime',
      transform: (time) => {
        if (!time) return '';
        // Ensure time is a Date object before calling toLocaleTimeString
        const dateObj = time instanceof Date ? time : new Date(time);
        if (isNaN(dateObj.getTime())) {
          return 'Last updated: Invalid date';
        }
        return `Last updated: ${dateObj.toLocaleTimeString()}`;
      }
    });

    console.log('Common UI bindings set up');
  }

  /**
   * Schedule an element update (batched)
   * @private
   * @param {string} bindingId - Binding identifier
   * @param {*} newValue - New value
   */
  _scheduleElementUpdate(bindingId, newValue) {
    const binding = this.bindings.get(bindingId);
    if (!binding) return;

    // Check if value actually changed
    if (this._isEqual(binding.lastValue, newValue)) {
      return;
    }

    binding.newValue = newValue;
    this.pendingUpdates.add(bindingId);

    if (binding.immediate) {
      this._updateElement(bindingId);
    } else {
      this._scheduleBatchUpdate();
    }
  }

  /**
   * Schedule a conditional update (batched)
   * @private
   * @param {string} bindingId - Binding identifier
   * @param {*} newValue - New value
   */
  _scheduleConditionalUpdate(bindingId, newValue) {
    const binding = this.conditionalBindings.get(bindingId);
    if (!binding) return;

    // Check if value actually changed
    if (this._isEqual(binding.lastValue, newValue)) {
      return;
    }

    binding.newValue = newValue;
    this.pendingUpdates.add(bindingId);
    this._scheduleBatchUpdate();
  }

  /**
   * Schedule a computed update (batched)
   * @private
   * @param {string} bindingId - Binding identifier
   */
  _scheduleComputedUpdate(bindingId) {
    this.pendingUpdates.add(bindingId);
    this._scheduleBatchUpdate();
  }

  /**
   * Schedule batch update
   * @private
   */
  _scheduleBatchUpdate() {
    if (this.updateTimer) {
      return; // Already scheduled
    }

    this.updateTimer = requestAnimationFrame(this._performBatchUpdate);
  }

  /**
   * Perform batched updates
   * @private
   */
  _performBatchUpdate() {
    if (this.isDestroyed) {
      return;
    }

    const startTime = performance.now();
    let updatesPerformed = 0;

    // Process all pending updates
    for (const bindingId of this.pendingUpdates) {
      try {
        if (bindingId.startsWith('conditional:')) {
          this._updateConditional(bindingId);
        } else if (bindingId.startsWith('computed:')) {
          this._updateComputed(bindingId);
        } else {
          this._updateElement(bindingId);
        }
        updatesPerformed++;
      } catch (error) {
        console.error('UI update error for binding:', bindingId, error);
      }
    }

    // Clear pending updates
    this.pendingUpdates.clear();
    this.updateTimer = null;

    // Update performance metrics
    const updateTime = performance.now() - startTime;
    this.updateCount += updatesPerformed;
    this.lastUpdateTime = updateTime;
    this.averageUpdateTime = (this.averageUpdateTime + updateTime) / 2;

    if (updatesPerformed > 0) {
      console.debug(`UI batch update: ${updatesPerformed} updates in ${updateTime.toFixed(2)}ms`);
    }
  }

  /**
   * Update a single element
   * @private
   * @param {string} bindingId - Binding identifier
   */
  _updateElement(bindingId) {
    const binding = this.bindings.get(bindingId);
    if (!binding) return;

    const element = document.querySelector(binding.selector);
    if (!element) {
      console.warn('Element not found for selector:', binding.selector);
      return;
    }

    const newValue = binding.newValue !== undefined ? binding.newValue : this.stateManager.get(binding.property);
    const transformedValue = binding.transform(newValue);

    // Update the element
    if (binding.attribute === 'textContent') {
      element.textContent = transformedValue;
    } else if (binding.attribute === 'innerHTML') {
      element.innerHTML = transformedValue;
    } else if (binding.attribute === 'className') {
      element.className = transformedValue;
    } else if (binding.attribute.startsWith('data-')) {
      element.setAttribute(binding.attribute, transformedValue);
    } else {
      element[binding.attribute] = transformedValue;
    }

    binding.lastValue = newValue;
    delete binding.newValue;
  }

  /**
   * Update a conditional binding
   * @private
   * @param {string} bindingId - Binding identifier
   */
  _updateConditional(bindingId) {
    const binding = this.conditionalBindings.get(bindingId);
    if (!binding) return;

    const element = document.querySelector(binding.selector);
    if (!element) {
      console.warn('Element not found for selector:', binding.selector);
      return;
    }

    const newValue = binding.newValue !== undefined ? binding.newValue : this.stateManager.get(binding.property);
    const condition = binding.conditions[newValue] || binding.conditions['default'];

    if (condition !== undefined) {
      if (binding.attribute === 'className') {
        element.className = condition;
      } else {
        element.setAttribute(binding.attribute, condition);
      }
    }

    binding.lastValue = newValue;
    delete binding.newValue;
  }

  /**
   * Update a computed binding
   * @private
   * @param {string} bindingId - Binding identifier
   */
  _updateComputed(bindingId) {
    const binding = this.computedBindings.get(bindingId);
    if (!binding) return;

    const element = document.querySelector(binding.selector);
    if (!element) {
      console.warn('Element not found for selector:', binding.selector);
      return;
    }

    // Get current values for all properties
    const values = binding.properties.map(prop => this.stateManager.get(prop));
    const computedValue = binding.computeFn(...values);

    // Check if computed value changed
    if (this._isEqual(binding.lastValue, computedValue)) {
      return;
    }

    // Update the element
    if (binding.attribute === 'textContent') {
      element.textContent = computedValue;
    } else if (binding.attribute === 'innerHTML') {
      element.innerHTML = computedValue;
    } else if (binding.attribute === 'className') {
      element.className = computedValue;
    } else {
      element[binding.attribute] = computedValue;
    }

    binding.lastValue = computedValue;
  }

  /**
   * Get performance metrics
   * @returns {Object} Performance metrics
   */
  getPerformanceMetrics() {
    return {
      updateCount: this.updateCount,
      lastUpdateTime: this.lastUpdateTime,
      averageUpdateTime: this.averageUpdateTime,
      bindingCount: this.bindings.size + this.conditionalBindings.size + this.computedBindings.size,
      pendingUpdates: this.pendingUpdates.size
    };
  }

  /**
   * Check if two values are equal
   * @private
   * @param {*} a - First value
   * @param {*} b - Second value
   * @returns {boolean} Whether values are equal
   */
  _isEqual(a, b) {
    if (a === b) return true;
    if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
    return false;
  }

  /**
   * Destroy the reactive system and clean up all resources
   */
  destroy() {
    if (this.isDestroyed) {
      return;
    }

    console.log('Destroying UIReactiveSystem...');

    // Cancel pending updates
    if (this.updateTimer) {
      cancelAnimationFrame(this.updateTimer);
      this.updateTimer = null;
    }

    // Unsubscribe from all state changes
    for (const unsubscribe of this.unsubscribeFunctions) {
      try {
        unsubscribe();
      } catch (error) {
        console.warn('Failed to unsubscribe:', error);
      }
    }

    // Clear all data structures
    this.bindings.clear();
    this.conditionalBindings.clear();
    this.computedBindings.clear();
    this.unsubscribeFunctions.clear();
    this.pendingUpdates.clear();

    this.isDestroyed = true;
    console.log('UIReactiveSystem destroyed');
  }
}